// src/components/ContentfulImage/ContentfulImage.tsx

import React, { useState } from 'react';
import { getContentfulImageUrl, getResponsiveImageUrls } from '../../config/contentful';
import './ContentfulImage.css';

interface ContentfulImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  quality?: number;
  format?: 'jpg' | 'png' | 'webp' | 'avif';
  fit?: 'pad' | 'fill' | 'scale' | 'crop' | 'thumb';
  focus?: 'top' | 'right' | 'left' | 'bottom' | 'top_right' | 'top_left' | 'bottom_right' | 'bottom_left' | 'face' | 'faces' | 'center';
  className?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
  fallbackSrc?: string;
  responsive?: boolean;
  sizes?: number[];
}

const ContentfulImage: React.FC<ContentfulImageProps> = ({
  src,
  alt,
  width,
  height,
  quality = 80,
  format,
  fit = 'fill',
  focus = 'center',
  className = '',
  style,
  loading = 'lazy',
  onLoad,
  onError,
  fallbackSrc,
  responsive = false,
  sizes = [300, 600, 900, 1200]
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleLoad = () => {
    setImageLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setImageError(true);
    onError?.();
  };

  // Get the optimized image URL
  const optimizedSrc = getContentfulImageUrl(src, {
    width,
    height,
    quality,
    format,
    fit,
    focus
  });

  // Get responsive URLs if needed
  const responsiveUrls = responsive ? getResponsiveImageUrls(src, sizes) : null;

  // Determine which image to show
  const imageToShow = imageError && fallbackSrc ? fallbackSrc : optimizedSrc;

  // If responsive, use srcSet
  const srcSet = responsive && responsiveUrls 
    ? responsiveUrls.map(({ width, url }) => `${url} ${width}w`).join(', ')
    : undefined;

  return (
    <div className={`contentful-image-container ${className}`} style={style}>
      <img
        src={imageToShow}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
        srcSet={srcSet}
        sizes={responsive ? "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" : undefined}
        className={`contentful-image ${imageLoaded ? 'loaded' : 'loading'}`}
      />
      {!imageLoaded && !imageError && (
        <div className="contentful-image-placeholder">
          <div className="contentful-image-skeleton" />
        </div>
      )}
    </div>
  );
};

export default ContentfulImage;
